import {
  Box,
  BoxProps,
  Button,
  FormControl,
  FormHelperText,
  keyframes,
  Link,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useLocation, Link as RouterLink } from 'react-router-dom';
import { useEffect, useState } from 'react';
import Input from 'src/components/input';
import FieldErrorMessage from 'src/components/input/FieldErrorMessage';
import { InputLabelInsideFormControl } from 'src/components/input/InputLabelInside';
import * as Yup from 'yup';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import useLogin from './hooks/useLogin';

const shakeAnimation = keyframes`
0% { margin-left: 0rem; }
25% { margin-left: 0.5rem; }
75% { margin-left: -0.5rem; }
100% { margin-left: 0rem; }
`;

interface LoginFormValues {
  email: string;
  password: string;
}

const validationSchema = Yup.object().shape({
  email: Yup.string().email("This email address isn't valid").required('Required'),
  password: Yup.string().required('Required'),
});

export default function LoginForm(props: BoxProps) {
  const { login, submissionError, isSubmitting } = useLogin();

  const queryParams = useLocation().search;
  const [resetSuccess, setResetSuccess] = useState(false);

  useEffect(() => {
    setResetSuccess(queryParams?.includes('resetSuccess'));
  }, [queryParams, setResetSuccess]);

  const methods = useForm<LoginFormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  return (
    <Box {...props}>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(login)}>
          <VStack spacing={['12px', '18px']}>
            <Box w="100%">
              <InputLabelInsideFormControl
                id="email"
                label="Your email address:"
                isInvalid={!!methods.formState.errors.email}
                errorMessage={String(methods.formState.errors.email?.message)}
              >
                <Input
                  {...methods.register('email', {
                    // ignore leading/trailing spaces in email field
                    setValueAs: value => value.trim(),
                  })}
                  variation="label-inside"
                  autoComplete="username"
                  placeholder="e.g. johnappleseed@example.com"
                />
              </InputLabelInsideFormControl>
            </Box>
            <Box w="100%">
              <InputLabelInsideFormControl
                label="Your password:"
                id="password"
                isInvalid={!!methods.formState.errors.password}
                errorMessage={String(methods.formState.errors.password?.message)}
              >
                <Input
                  {...methods.register('password')}
                  variation="password"
                  autoComplete="current-password"
                  placeholder="e.g. ●●●●●●●●●●●●●"
                />
              </InputLabelInsideFormControl>
            </Box>
          </VStack>
          <FormControl isInvalid={!!submissionError} mb={submissionError ? '12px' : 0}>
            {resetSuccess && (
              <FormHelperText mb="12px">Your password has been reset successfully!</FormHelperText>
            )}
            <FieldErrorMessage>{submissionError}</FieldErrorMessage>
          </FormControl>
          <Text size="p3" textAlign="right" mt="10px">
            <Link
              as={RouterLink}
              to={`/forgot?email=${encodeURIComponent(methods.watch('email'))}`}
            >
              Forgot that pesky password?
            </Link>
          </Text>
          <Button
            data-cy="login"
            width="100%"
            height="54px"
            type="submit"
            isDisabled={isSubmitting}
            mt="27px"
            size="lg"
            animation={submissionError ? `${shakeAnimation} 0.2s ease-in-out 0s 2` : undefined}
          >
            {isSubmitting ? 'Please wait...' : 'Login'}
          </Button>
        </form>
      </FormProvider>
    </Box>
  );
}
